import { FC, Fragment, useCallback, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"

import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import { Divider } from "@mui/material"

import { Button, Menu, MenuItem, Typography } from "@synapse-analytics/synapse-ui"
import { NotificationUtils } from "@synapse-analytics/synapse-ui"

import { VisionAPI } from "../../../../API/VisionAPI"
import Placeholder from "../../../../components/Placeholder"
import WarningDialog from "../../../../components/WarningDialog"
import { useBranchesStore } from "../../../../store"
import { definitions } from "../../../../types/Generated/apiTypes"
import BranchItem from "./components/BranchItem"
import CollectionNameRow from "./components/CollectionNameRow"

import styles from "./CollectionsTab.module.scss"

type Branch = definitions["Branch"]
type Collection = definitions["Collection"]

/**
 * Props for the CollectionsTab component.
 */
interface CollectionsTabProps {
  /** Array of uncollected branches available for adding to collections */
  branches?: Branch[]
  /** Loading state for branches data */
  areBranchesLoading?: boolean
  /** Callback invoked when the "Add New Collection" button is clicked */
  onAddNewCollection: () => void
}

/**
 * CollectionsTab component displays and manages collections of branches.
 *
 * Features:
 * - Displays list of collections with their branches
 * - Allows selecting/deselecting branches via checkboxes
 * - Edit mode for renaming and deleting collections
 * - Add branches to collections via menu
 * - Delete collections with confirmation dialog
 *
 * @param props - The component props
 * @returns The rendered collections tab component
 */
const CollectionsTab: FC<CollectionsTabProps> = ({ branches, areBranchesLoading, onAddNewCollection }) => {
  const queryClient = useQueryClient()
  const [selectedBranches, setSelectedBranches] = useBranchesStore((state) => [
    state.selectedBranches ?? [],
    state.setSelectedBranches,
  ])
  const [isEditMode, setIsEditMode] = useState(false)
  const [addBranchMenuAnchor, setAddBranchMenuAnchor] = useState<HTMLElement | null>(null)
  const [currentCollectionId, setCurrentCollectionId] = useState<number | null>(null)
  const [selectedBranchesForMenu, setSelectedBranchesForMenu] = useState<number[]>([])
  const [isDeleteCollectionDialogOpen, setIsDeleteCollectionDialogOpen] = useState(false)
  const [collectionToDelete, setCollectionToDelete] = useState<{ id: number; name: string } | null>(null)
  const addBranchButtonRef = useRef<HTMLButtonElement>(null)

  const { data: collections, isLoading: areCollectionsLoading } = useQuery<Collection[]>("fetchCollections", () =>
    VisionAPI.fetchCollections()
  )

  /**
   * Memoized Set of selected branch IDs for O(1) membership checks.
   * Used to efficiently determine if a branch is selected.
   */
  const selectedBranchesSet = useMemo(() => new Set(selectedBranches), [selectedBranches])
  /**
   * Memoized Set of branch IDs selected in the add branch menu.
   * Used for efficient membership checks in the menu.
   */
  const selectedBranchesForMenuSet = useMemo(() => new Set(selectedBranchesForMenu), [selectedBranchesForMenu])

  /**
   * Handles collection checkbox change - selects or deselects all branches in the collection.
   *
   * @param collection - The collection whose checkbox was toggled
   * @param checked - Whether the checkbox is checked (select all) or unchecked (deselect all)
   */
  const handleCollectionCheckboxChange = (collection: Collection, checked: boolean) => {
    const collectionBranchIds = collection.branches?.map((branch) => branch.id!) ?? []
    const collectionBranchIdsSet = new Set(collectionBranchIds)
    if (checked) {
      // Add all collection branches to selected branches
      const newSelectedBranches = [...new Set([...selectedBranches, ...collectionBranchIds])]
      setSelectedBranches(newSelectedBranches)
    } else {
      // Remove all collection branches from selected branches
      const newSelectedBranches = selectedBranches.filter((id) => !collectionBranchIdsSet.has(id))
      setSelectedBranches(newSelectedBranches)
    }
  }

  /**
   * Toggles edit mode on/off for collections.
   */
  const handleEditModeToggle = () => {
    setIsEditMode(!isEditMode)
  }

  /**
   * Mutation hook for deleting a collection.
   * Invalidates collections and uncollected branches queries on success.
   */
  const { mutate: deleteCollectionMutation } = useMutation(
    (collectionId: number) => VisionAPI.deleteCollection(collectionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("fetchCollections")
        queryClient.invalidateQueries("fetchUncollectedBranches")
        NotificationUtils.toast("Collection deleted successfully", {
          severity: "success",
        })
        setIsDeleteCollectionDialogOpen(false)
        setCollectionToDelete(null)
      },
      onError: (error: any) => {
        NotificationUtils.toast(`Failed to delete collection: ${error?.message || "Unknown error"}`, {
          severity: "error",
        })
      },
    }
  )

  /**
   * Handles delete collection action - opens the warning confirmation dialog.
   *
   * @param collection - The collection to delete
   */
  const handleDeleteCollection = (collection: Collection) => {
    if (collection.id && collection.name) {
      setCollectionToDelete({ id: collection.id, name: collection.name })
      setIsDeleteCollectionDialogOpen(true)
    }
  }

  /**
   * Confirms collection deletion and triggers the delete mutation.
   */
  const handleConfirmDeleteCollection = () => {
    if (collectionToDelete) {
      deleteCollectionMutation(collectionToDelete.id)
    }
  }

  /**
   * Cancels collection deletion and closes the dialog.
   */
  const handleCancelDeleteCollection = () => {
    setIsDeleteCollectionDialogOpen(false)
    setCollectionToDelete(null)
  }

  /**
   * Mutation hook for updating collection branches.
   * Used when adding branches to a collection via the menu.
   * Invalidates collections and uncollected branches queries on success.
   */
  const { mutate: updateCollectionBranches, isLoading: isUpdatingBranches } = useMutation(
    ({ collectionId, branches_ids, name }: { collectionId: number; branches_ids: number[]; name: string }) => {
      return VisionAPI.updateCollection(collectionId, { name, branches_ids })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("fetchCollections")
        queryClient.invalidateQueries("fetchUncollectedBranches")
        NotificationUtils.toast("Collection branches updated successfully", {
          severity: "success",
        })
      },
      onError: (error: any) => {
        NotificationUtils.toast(`Failed to update collection branches: ${error?.message || "Unknown error"}`, {
          severity: "error",
        })
      },
    }
  )

  /**
   * Opens the add branch menu for a specific collection.
   *
   * @param collection - The collection to add branches to
   * @param event - The mouse event that triggered the menu
   */
  const handleOpenAddBranchMenu = (collection: Collection, event: React.MouseEvent<HTMLButtonElement>) => {
    if (!collection.id) return
    event.stopPropagation()
    setCurrentCollectionId(collection.id)
    setAddBranchMenuAnchor(event.currentTarget)
    setSelectedBranchesForMenu([])
  }

  /**
   * Closes the add branch menu and resets related state.
   */
  const handleCloseAddBranchMenu = () => {
    setAddBranchMenuAnchor(null)
    setCurrentCollectionId(null)
    setSelectedBranchesForMenu([])
  }

  /**
   * Toggles branch selection in the add branch menu.
   * Memoized to avoid recreating on every render.
   *
   * @param branchId - The ID of the branch to toggle
   */
  const handleToggleBranchSelection = useCallback((branchId: number) => {
    setSelectedBranchesForMenu((prev) => {
      const prevSet = new Set(prev)
      if (prevSet.has(branchId)) {
        return prev.filter((id) => id !== branchId)
      } else {
        return [...prev, branchId]
      }
    })
  }, [])

  /**
   * Handles branch checkbox toggle - adds or removes branch from selected branches.
   * Memoized to avoid recreating inline handlers.
   *
   * @param branchId - The ID of the branch to toggle
   * @param isChecked - Whether the branch is being selected (true) or deselected (false)
   */
  const handleToggleBranch = useCallback(
    (branchId: number, isChecked: boolean) => {
      if (isChecked) {
        setSelectedBranches([...selectedBranches, branchId])
      } else {
        setSelectedBranches(selectedBranches.filter((id) => id !== branchId))
      }
    },
    [selectedBranches, setSelectedBranches]
  )

  /**
   * Memoized menu items for the add branch menu.
   * Prevents re-mapping branches on every render.
   */
  const branchesMenuItems = useMemo(() => {
    if (!branches || branches.length === 0) return null
    return branches.map((branch) => (
      <MenuItem
        key={branch.id}
        isSelected={selectedBranchesForMenuSet.has(branch.id!)}
        onClick={() => handleToggleBranchSelection(branch.id!)}
      >
        <Typography variant="p">{branch.name}</Typography>
      </MenuItem>
    ))
  }, [branches, selectedBranchesForMenuSet, handleToggleBranchSelection])

  /**
   * Handles adding selected branches from the menu to the current collection.
   * Merges existing branches with newly selected ones, preventing duplicates.
   * Closes the menu on success.
   */
  const handleAddBranchesToCollection = () => {
    if (currentCollectionId === null || selectedBranchesForMenu.length === 0) return

    const collection = collections?.find((c) => c.id === currentCollectionId)
    if (!collection) return

    // Get existing branch IDs and merge with new ones, preventing duplicates
    const existingBranchIds = new Set((collection.branches ?? []).map((b) => b.id!))
    selectedBranchesForMenu.forEach((id) => existingBranchIds.add(id))

    updateCollectionBranches(
      {
        collectionId: collection.id!,
        branches_ids: Array.from(existingBranchIds),
        name: collection.name,
      },
      {
        onSuccess: () => {
          handleCloseAddBranchMenu()
        },
      }
    )
  }

  return (
    <Fragment>
      <div className={styles.checkboxContainer}>
        {areBranchesLoading || areCollectionsLoading ? (
          <div className={styles.emptyListContainer}>
            <Placeholder svgHeight="75px" svgWidth="62px" hasBackground={false} isLoading />
          </div>
        ) : collections?.length === 0 ? (
          <div className={styles.emptyListContainer}>
            <Placeholder
              svgHeight="75px"
              svgWidth="62px"
              hasBackground={false}
              description="No Collections Found"
              descriptionVariant="a"
              subDescription="Start by creating your first collection!"
              subDescriptionVariant="label-regular"
            />
          </div>
        ) : (
          collections?.map((collection, collectionIndex) => (
            <Fragment key={collection.id || collectionIndex}>
              <div className={styles.collectionCheckboxWrapper}>
                <div className={styles.collectionNameRow}>
                  <CollectionNameRow
                    collection={collection}
                    collectionIndex={collectionIndex}
                    isEditMode={isEditMode}
                    selectedBranchesSet={selectedBranchesSet}
                    onCheckboxChange={handleCollectionCheckboxChange}
                    onDelete={handleDeleteCollection}
                  />
                </div>
                <div className={styles.branchesCheckboxWrapper}>
                  {collection.branches?.map((branch) => (
                    <div key={branch.id} className={styles.branchCheckboxWrapper}>
                      <BranchItem
                        branch={branch}
                        collection={collection}
                        isEditMode={isEditMode}
                        selectedBranchesSet={selectedBranchesSet}
                        onToggle={handleToggleBranch}
                      />
                    </div>
                  ))}
                  {isEditMode && (
                    <div className={styles.addBranchButton}>
                      <Button
                        ref={addBranchButtonRef}
                        size="small"
                        variant="ghost"
                        startIcon={<AddIcon fontSize="small" />}
                        className={styles.iconButton}
                        onClick={(e) => handleOpenAddBranchMenu(collection, e as React.MouseEvent<HTMLButtonElement>)}
                      />
                    </div>
                  )}
                </div>
              </div>
              {collectionIndex < collections.length - 1 && <Divider className={styles.divider} />}
            </Fragment>
          ))
        )}
      </div>
      <Menu
        open={Boolean(addBranchMenuAnchor && currentCollectionId !== null)}
        onClose={handleCloseAddBranchMenu}
        anchorEl={addBranchMenuAnchor}
        menuMaxContent
        className={styles.addBranchMenu}
      >
        {branches?.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="p">No available branches</Typography>
          </MenuItem>
        ) : (
          <div className={styles.addBranchMenuContent}>
            <div className={styles.addBranchMenuHeaderContainer}>
              <Typography variant="label-regular" variantColor={2} className={styles.addBranchMenuHeader}>
                Branches not in any collection
              </Typography>

              <CloseIcon onClick={handleCloseAddBranchMenu} className={`${styles.buttonIcon} ${styles.closeIcon}`} />
            </div>
            <div className={styles.addBranchMenuItems}>{branchesMenuItems}</div>
            <Button
              variant="primary"
              size="small"
              fullWidth
              isLoading={isUpdatingBranches}
              disabled={selectedBranchesForMenu.length === 0 || isUpdatingBranches}
              onClick={handleAddBranchesToCollection}
              className={styles.addBranchButton}
            >
              Add to Collection
            </Button>
          </div>
        )}
      </Menu>
      <div className={styles.buttonContainer}>
        <Button
          color="secondary"
          variant="secondary"
          size="small"
          fullWidth
          onClick={handleEditModeToggle}
          disabled={collections?.length === 0}
        >
          {isEditMode ? "Cancel Collections Edit" : "Edit Collections"}
        </Button>
        <Button color="primary" variant="primary" size="small" fullWidth onClick={onAddNewCollection}>
          Add New Collection
        </Button>
      </div>
      <WarningDialog
        isOpen={isDeleteCollectionDialogOpen}
        onConfirm={handleConfirmDeleteCollection}
        onCancel={handleCancelDeleteCollection}
        dialogTitle={collectionToDelete ? `Delete '${collectionToDelete.name}'?` : "Delete Collection?"}
        content={
          collectionToDelete
            ? `Deleting ${collectionToDelete.name} will remove it along with all its included branches. Are you sure you want to proceed?`
            : "Deleting this collection will remove it along with all its included branches. Are you sure you want to proceed?"
        }
        actionTitle="Delete"
        isDangerousAction
      />
    </Fragment>
  )
}

export default CollectionsTab
